import React, { useCallback, useEffect, useContext, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import { SpotlightManager, SpotlightTarget, SpotlightTransition } from '@atlaskit/onboarding';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { type Actions } from '@atlaskit/onboarding/types';
import { useThemeObserver } from '@atlaskit/tokens';

import { useChoreographerAssets } from '@confluence/choreographer-services';
import { useLoomEntryPointVariant } from '@confluence/loom-utils/entry-points/useLoomEntryPointVariant';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext';

import { messages } from '../messages';
import { useLoomScriptReadTimeContext } from '../context/LoomScriptReadTimeContext';
import { useLoomScriptCrossFlowContext } from '../context/LoomScriptCrossFlowContext';

const SPOTLIGHT_HERO_IMAGE_LIGHT_MODE =
	'https://dam-cdn.atl.orangelogic.com/AssetLink/6806167qo2vo6mbpa07g0ej68354uoqy.svg';

const SPOTLIGHT_HERO_IMAGE_DARK_MODE =
	'https://dam-cdn.atl.orangelogic.com/AssetLink/t128d072e58nrvt75mf6l54p23tlyyp4.svg';

export type LoomScriptExtensionSpotlightWrapperProps = {
	children: React.ReactNode;
	/**
	 * Used for analytics purposes only and is not used in any logic
	 */
	readTimeSeconds?: number;
};

export const LoomScriptExtensionSpotlightWrapper = ({
	children,
	readTimeSeconds,
}: LoomScriptExtensionSpotlightWrapperProps) => {
	const { isLoomScriptReadTimeSpotlightShown, dismissLoomScriptReadTimeSpotlight } =
		useLoomScriptReadTimeContext();
	const { formatMessage } = useIntl();
	const { Spotlight } = useChoreographerAssets();
	const { colorMode } = useThemeObserver();
	/**
	 * The images are ~100kB so we want to pre-send a fetch for them during the delay
	 * to minimise any lag we see
	 */
	useEffect(() => {
		switch (colorMode) {
			case 'dark':
				new Image().src = SPOTLIGHT_HERO_IMAGE_DARK_MODE;
				break;
			case 'light':
				new Image().src = SPOTLIGHT_HERO_IMAGE_LIGHT_MODE;
				break;
		}
	}, [colorMode]);

	const { entryPointVariant } = useLoomEntryPointVariant();
	const { isSiteAdmin } = useContext(SPAViewContext);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { openLoomCrossFlow } = useLoomScriptCrossFlowContext();

	useEffect(() => {
		if (isLoomScriptReadTimeSpotlightShown) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'rendered',
					actionSubject: 'loomScriptReadTimeDropDownExtensionSpotlight',
					product: 'confluence',
					source: 'loomScriptReadTimeDropDownExtensionSpotlight',
					attributes: {
						entryPointVariant,
						readTimeSeconds,
						isSiteAdmin,
					},
				},
			}).fire();
		}
	}, [
		isLoomScriptReadTimeSpotlightShown,
		createAnalyticsEvent,
		entryPointVariant,
		readTimeSeconds,
		isSiteAdmin,
	]);

	const handleLoomScriptReadTimeSpotlightDismissal = useCallback(() => {
		dismissLoomScriptReadTimeSpotlight();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'loomScriptReadTimeDropDownExtensionSpotlight',
				product: 'confluence',
				source: 'loomScriptReadTimeDropDownExtensionSpotlight',
				attributes: {
					entryPointVariant,
					readTimeSeconds,
					isSiteAdmin,
				},
			},
		}).fire();
	}, [
		createAnalyticsEvent,
		dismissLoomScriptReadTimeSpotlight,
		entryPointVariant,
		readTimeSeconds,
		isSiteAdmin,
	]);

	/**
	 * Note: this should only be consumed if the `entryPointVariant` is `CROSS_FLOW`
	 */
	const handleLoomScriptReadTimeSpotlightTryNow = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'loomScriptReadTimeDropDownExtensionSpotlightTryNowButton',
				product: 'confluence',
				source: 'loomScriptReadTimeDropDownExtensionSpotlight',
				attributes: {
					entryPointVariant,
					readTimeSeconds,
					isSiteAdmin,
				},
			},
		}).fire();
		handleLoomScriptReadTimeSpotlightDismissal();
		openLoomCrossFlow();
	}, [
		handleLoomScriptReadTimeSpotlightDismissal,
		openLoomCrossFlow,
		createAnalyticsEvent,
		entryPointVariant,
		readTimeSeconds,
		isSiteAdmin,
	]);

	const spotlightActions: Actions = useMemo(() => {
		if (entryPointVariant === 'CROSS_FLOW')
			return [
				{
					text: formatMessage(messages.spotlightTryNowButtonLabel),
					onClick: handleLoomScriptReadTimeSpotlightTryNow,
				},
				{
					/**
					 * Dismiss is no longer the primary call to action if we are cross flow
					 */
					appearance: 'subtle',
					text: formatMessage(messages.spotlightButtonDismissLabel),
					onClick: handleLoomScriptReadTimeSpotlightDismissal,
				},
			];

		return [
			{
				text: formatMessage(messages.spotlightButtonLabel),
				onClick: handleLoomScriptReadTimeSpotlightDismissal,
			},
		];
	}, [
		formatMessage,
		handleLoomScriptReadTimeSpotlightDismissal,
		handleLoomScriptReadTimeSpotlightTryNow,
		entryPointVariant,
	]);

	return (
		<SpotlightManager>
			<SpotlightTarget name="loomAIScriptSection">{children}</SpotlightTarget>
			<SpotlightTransition>
				{isLoomScriptReadTimeSpotlightShown && (
					<Spotlight
						image={
							colorMode === 'light'
								? SPOTLIGHT_HERO_IMAGE_LIGHT_MODE
								: SPOTLIGHT_HERO_IMAGE_DARK_MODE
						}
						heading={formatMessage(messages.spotlightHeading)}
						target="loomAIScriptSection"
						dialogPlacement="left bottom"
						dialogWidth={275}
						actions={spotlightActions}
					>
						<FormattedMessage {...messages.spotlightBody} />
					</Spotlight>
				)}
			</SpotlightTransition>
		</SpotlightManager>
	);
};
